import React from 'react';
import DropdownSelect from '../Visualeffects/DropdownSelect';
import ExpandCollapse from '../Visualeffects/ExpandCollapse';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';

const parseMatrix = (input) => {
  if (Array.isArray(input)) {
    return input.map((row) => (Array.isArray(row) ? row.map(Number) : [Number(row)]));
  }
  if (typeof input !== 'string') return [];
  const trimmed = input.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return parseMatrix(parsed);
  } catch {
    const rows = trimmed.split(/\n+/).map((line) => line.trim()).filter(Boolean);
    return rows.map((row) =>
      row
        .split(/[,\s]+/)
        .filter(Boolean)
        .map((cell) => Number(cell))
    );
  }
};

const parseVector = (input) => {
  if (Array.isArray(input)) return input.map(Number);
  if (typeof input !== 'string') return [];
  const trimmed = input.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return parseVector(parsed);
  } catch {
    return trimmed
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((cell) => Number(cell));
  }
};

const parseRelations = (input) => {
  if (Array.isArray(input)) {
    return input.map((entry) => String(entry));
  }
  if (typeof input !== 'string') return [];
  const trimmed = input.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    return parseRelations(parsed);
  } catch {
    return trimmed
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((cell) => String(cell));
  }
};

const normalizeRelation = (relation) =>
  relation.replace('≤', '<=').replace('≥', '>=').trim();

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value ?? '');
  return Math.abs(num) < 1e-9 ? '0' : num.toFixed(4);
};

const getResultBoxClasses = (status) => {
  if (!status) return 'bg-white/80 border border-white/40 text-gray-800 shadow-lg';
  if (status === 'optimal') return 'bg-green-50 border border-green-200 text-green-800 shadow-lg';
  if (status === 'unbounded' || status === 'infeasible' || status === 'iteration_limit') {
    return 'bg-red-50 border border-red-200 text-red-800 shadow-lg';
  }
  return 'bg-amber-50 border border-amber-200 text-amber-700 shadow-lg';
};

export default function SimplexSolverUI({ state = {}, setState }) {
  const {
    direction = 'max',
    useNormalForm = true,
    objectiveInput = '',
    matrixInput = '',
    rhsInput = '',
    relationInput = '',
    loading = false,
    result = null,
    error = null,
    matrixError = false,
    rhsError = false,
    objectiveError = false,
    relationError = false,
  } = state;

  const set = (key, value) => setState((prev) => ({ ...prev, [key]: value }));
  const [showAllSteps, setShowAllSteps] = React.useState(false);

  React.useEffect(() => {
    setShowAllSteps(false);
  }, [result]);

  const handleSolve = async () => {
    setShowAllSteps(false);
    set('loading', true);
    set('error', null);
    set('matrixError', false);
    set('rhsError', false);
    set('objectiveError', false);
    set('relationError', false);

    let matrix;
    let rhs;
    let objective;
    let relations;

    try {
      objective = parseVector(objectiveInput);
      if (!objective.length || objective.some((value) => !Number.isFinite(value))) {
        throw new Error('Objective coefficients must be numeric.');
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse objective coefficients.');
      set('objectiveError', true);
      return;
    }

    try {
      matrix = parseMatrix(matrixInput);
      if (!matrix.length || matrix.some((row) => row.length !== matrix[0].length)) {
        throw new Error('Matrix A must be rectangular.');
      }
      if (matrix.some((row) => row.some((value) => !Number.isFinite(value)))) {
        throw new Error('Matrix A entries must be numeric.');
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse matrix A.');
      set('matrixError', true);
      return;
    }

    if (matrix[0].length !== objective.length) {
      set('loading', false);
      set('error', 'Objective length must match number of columns in A.');
      set('objectiveError', true);
      return;
    }

    try {
      rhs = parseVector(rhsInput);
      if (rhs.length !== matrix.length) {
        throw new Error('Vector b length must match number of constraints.');
      }
      if (rhs.some((value) => !Number.isFinite(value))) {
        throw new Error('Vector b entries must be numeric.');
      }
    } catch (err) {
      set('loading', false);
      set('error', err.message || 'Unable to parse vector b.');
      set('rhsError', true);
      return;
    }

    if (useNormalForm) {
      relations = Array.from({ length: matrix.length }, () => '<=');
    } else {
      try {
        relations = parseRelations(relationInput).map(normalizeRelation);
        if (relations.length !== matrix.length) {
          throw new Error('Relation count must match number of constraints.');
        }
        if (relations.some((value) => !['<=', '>=', '='].includes(value))) {
          throw new Error('Relations must be <=, =, or >=.');
        }
      } catch (err) {
        set('loading', false);
        set('error', err.message || 'Unable to parse constraint relations.');
        set('relationError', true);
        return;
      }
    }

    try {
      const response = await fetch('https://api.numsolve.org/simplex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objective,
          constraints: matrix,
          rhs,
          relations,
          maximize: direction === 'max',
          solver_options: {
            pivotRule: 'dantzig',
          },
        }),
      });

      const data = await response.json();
      if (data?.error) {
        set('error', data.error);
        set('result', null);
      } else {
        set('result', data);
        set('error', null);
        try {
          const recentEntry = {
            timestamp: new Date().toISOString(),
            method: 'simplex',
            input: {
              objective,
              constraints: matrix,
              rhs,
              relations,
              maximize: direction === 'max',
              solver_options: { pivotRule: 'dantzig' },
            },
            solution: data?.solution ?? null,
            objective_value: data?.objective_value ?? null,
            status: data?.status ?? null,
            log: Array.isArray(data?.steps) ? data.steps : null,
          };
          const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
          const updated = [recentEntry, ...existing].slice(0, 100);
          localStorage.setItem('calcLogs', JSON.stringify(updated));
          window.dispatchEvent(new Event('calcLogsUpdated'));
        } catch {
          // ignore logging errors
        }
      }
    } catch (err) {
      set('error', 'Backend not reachable.');
    }

    set('loading', false);
  };

  const steps = Array.isArray(result?.steps) ? result.steps : [];
  const VISIBLE_STEPS = 3;
  const headSteps = steps.slice(0, VISIBLE_STEPS);
  const tailSteps = steps.slice(VISIBLE_STEPS);

  const renderTableau = (step) => {
    const variableNames = step.variableNames || [];
    const pivotRow = step.pivotPosition?.row ?? null;
    const pivotCol = step.pivotPosition?.column ?? null;
    const rowTypes = step.rowTypes || [];
    const rowLabels = step.rowLabels || [];

    return (
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/70">
        <table className="w-full border-collapse text-xs text-gray-800">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-2 py-2 text-left font-semibold">Basis</th>
              {variableNames.map((name, colIdx) => (
                <th
                  key={`${name}-${colIdx}`}
                  className={`px-2 py-2 text-right font-semibold ${
                    pivotCol === colIdx ? 'bg-red-100 text-red-700' : ''
                  }`}
                >
                  {name}
                </th>
              ))}
              <th className="px-2 py-2 text-right font-semibold">RHS</th>
            </tr>
          </thead>
          <tbody>
            {step.tableau.map((row, rowIdx) => {
              const rowType = rowTypes[rowIdx] || null;
              const isObjective = rowType ? rowType === 'objective' : rowIdx === step.tableau.length - 1;
              const isSummary = rowType?.startsWith('summary');
              const basisLabel = rowLabels[rowIdx] ?? (isObjective ? 'z' : step.basicVariables?.[rowIdx] ?? '');
              return (
                <tr
                  key={`row-${rowIdx}`}
                  className={`${
                    pivotRow === rowIdx && !isSummary ? 'bg-red-50/70' : ''
                  } ${isObjective ? 'border-t border-slate-200 bg-slate-50' : ''} ${
                    isSummary ? 'border-t border-slate-200 bg-slate-100/80 font-semibold' : ''
                  }`}
                >
                  <td className="px-2 py-2 font-semibold text-left">{basisLabel}</td>
                  {row.slice(0, -1).map((cell, colIdx) => (
                    <td
                      key={`${rowIdx}-${colIdx}`}
                      className={`px-2 py-2 text-right ${
                        pivotCol === colIdx && !isSummary ? 'bg-red-100 text-red-700' : ''
                      } ${pivotRow === rowIdx && pivotCol === colIdx && !isSummary ? 'font-bold' : ''}`}
                    >
                      {formatNumber(cell)}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right font-semibold">{formatNumber(row[row.length - 1])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStep = (step, idx) => (
    <div key={`${step.phase}-${idx}`} className="space-y-3 border-b border-white/20 pb-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-800">
          {step.phase} · Iteration {step.iterationIndex}
        </div>
        {step.enteringVariable && (
          <div className="text-xs font-semibold text-red-600">
            Pivot: {step.enteringVariable} ↦ {step.leavingVariable}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-600">{step.description}</p>
      {renderTableau(step)}
    </div>
  );

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <div className="space-y-4">
        <DropdownSelect
          label="Objective"
          color="red"
          value={direction}
          onChange={(value) => set('direction', value)}
          options={[
            { value: 'max', label: 'Maximize' },
            { value: 'min', label: 'Minimize' },
          ]}
        />

        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Objective coefficients</label>
          <div className="input-wrapper input-wrapper-tight input-pulse-rose">
            <input
              type="text"
              value={objectiveInput}
              onChange={(e) => set('objectiveInput', e.target.value)}
              placeholder="e.g. [3, 2]"
              className={`w-full rounded-xl border px-4 py-3 text-gray-800 focus:outline-none ${
                objectiveError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
              }`}
            />
          </div>
        </div>

        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Constraint matrix A</label>
          <div className="input-wrapper input-wrapper input-pulse-rose">
            <textarea
              value={matrixInput}
              onChange={(e) => set('matrixInput', e.target.value)}
              placeholder="e.g. [[1, 1], [1, 0], [0, 1]]"
              rows={4}
              className={`w-full resize-none rounded-xl border px-4 py-2 text-gray-800 ${
                matrixError ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50'
              }`}
            />
          </div>
        </div>

        <div>
          <label className="mb-3 block text-sm font-bold text-gray-700">Constraint vector b</label>
          <div className="input-wrapper input-wrapper-tight input-pulse-rose">
            <input
              type="text"
              value={rhsInput}
              onChange={(e) => set('rhsInput', e.target.value)}
              placeholder="e.g. [4, 2, 3]"
              className={`w-full rounded-xl border px-4 py-3 text-gray-800 focus:outline-none ${
                rhsError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
              }`}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <div>
            <p className="font-semibold text-gray-800">Normal form (Ax ≤ b)</p>
            <p className="text-xs text-gray-500">Use ≤ for every constraint.</p>
          </div>
          <button
            type="button"
            onClick={() => set('useNormalForm', !useNormalForm)}
            className={`relative h-7 w-12 rounded-full transition ${
              useNormalForm ? 'bg-red-500' : 'bg-gray-300'
            }`}
            aria-pressed={useNormalForm}
            aria-label="Toggle normal form"
          >
            <span
              className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white transition ${
                useNormalForm ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {!useNormalForm && (
          <div>
            <label className="mb-3 block text-sm font-bold text-gray-700">Relation signs</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-rose">
              <input
                type="text"
                value={relationInput}
                onChange={(e) => set('relationInput', e.target.value)}
                placeholder='e.g. ["<=", "<=", "<="]'
                className={`w-full rounded-xl border px-4 py-3 text-gray-800 focus:outline-none ${
                  relationError ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
                }`}
              />
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Variables are assumed non-negative by default (x ≥ 0). Use ≤, =, ≥ in the same order as the
          constraints.
        </p>
      </div>

      <div className="mt-6">
        <button className="button button-red" onClick={handleSolve} disabled={loading}>
          {loading ? 'Solving…' : 'Solve'}
        </button>
      </div>
    </div>
  );

  const outputCard = (
    <div className={`rounded-3xl p-8 ${getResultBoxClasses(result?.status)}`}>
      <div className="space-y-4 text-sm">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Simplex Output</h3>
          <p className="text-xs text-gray-600">
            {result?.status ? `Status: ${result.status}` : 'Solve a linear program to see steps.'}
          </p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {result?.status === 'optimal' && (
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              <span className="font-semibold">Optimal solution:</span>{' '}
              {Array.isArray(result.solution) ? `[${result.solution.map(formatNumber).join(', ')}]` : 'n/a'}
            </p>
            <p>
              <span className="font-semibold">Optimal objective value:</span>{' '}
              {formatNumber(result.objective_value)}
            </p>
          </div>
        )}

        {result?.status && result?.status !== 'optimal' && !error && (
          <p className="text-sm text-red-600">{result.message || 'No feasible optimum found.'}</p>
        )}

        {steps.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Tableau steps</h4>
            <div className="space-y-4">
              {headSteps.map(renderStep)}
              {tailSteps.length > 0 && (
                <ExpandCollapse open={showAllSteps}>
                  <div className="space-y-4">{tailSteps.map(renderStep)}</div>
                </ExpandCollapse>
              )}
            </div>
            {tailSteps.length > 0 && (
              <button
                type="button"
                onClick={() => setShowAllSteps((prev) => !prev)}
                className="text-xs font-semibold text-red-600"
              >
                {showAllSteps ? 'Hide steps' : `Show ${tailSteps.length} more steps`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <SolverTwoPanelLayout
      inputCard={inputCard}
      outputCard={outputCard}
      loading={loading}
    />
  );
}
